import { PAGINATION_LIMIT_VALUE_NUM, PAGINATION_NUM_VALUE_NUM } from 'constants/headers'
import { getPaginationHeaders } from 'tools/headers'
import request from 'tools/request'
import { DocumentListResponse } from 'types/document'
import { InvoiceGroupSingletonResponse } from 'types/invoiceGroup'
import {
  AmortizationPreviewEntry,
  AmortizationPreviewRequest,
  AmortizationPreviewResponse,
  ApproveForFundingOverAdvancesRequest,
  ApproveForFundingOverAdvancesSummaryResponse,
  CreateOverAdvanceRequest,
  OverAdvanceApproveForFundingSummaryParams,
  OverAdvanceGetCollectionResponse,
  OverAdvanceListParams,
  OverAdvanceListTotalsResponse,
  OverAdvanceSingletonResponse,
  OverAdvanceInfo,
  OverAdvancePayoff,
  RegeneratePaymentPlanRequest,
  UpdateOverAdvanceRequest,
  UpdatePlannedPaymentRequest,
} from 'types/overAdvance'

export const listOverAdvances = async (
  params: OverAdvanceListParams,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM
) => {
  if (params && params.q) {
    params.q = params.q.trim()
  }
  const data = await request
    .get<OverAdvanceGetCollectionResponse>('over-advances', {
      params: params,
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => {
      return {
        headers: res.headers,
        data: res.data.overAdvances,
      }
    })
  return data
}

export const listOverAdvancesTotals = async (params: OverAdvanceListParams) => {
  if (params && params.q) {
    params.q = params.q.trim()
  }
  const data = await request
    .get<OverAdvanceListTotalsResponse>('over-advances/list-totals', {
      params: params,
    })
    .then((res) => {
      return res.data.totals
    })
  return data
}

export const previewAmortizationSchedule = async (
  payload: AmortizationPreviewRequest
): Promise<AmortizationPreviewEntry[]> => {
  return await request
    .post<AmortizationPreviewResponse>('over-advances/amortization-preview', payload)
    .then((res) => res.data.plannedPayments)
}

export const createOverAdvance = async (payload: CreateOverAdvanceRequest) => {
  return await request.post<OverAdvanceSingletonResponse>('over-advances', payload)
}

export const getOverAdvance = async (overAdvanceId: string) => {
  const data = await request.get<OverAdvanceSingletonResponse>(`over-advances/${overAdvanceId}`).then((res) => {
    return res.data.overAdvance
  })
  return data
}

export const updateOverAdvance = async (overAdvanceId: string, payload: UpdateOverAdvanceRequest) => {
  const data = await request
    .put<OverAdvanceSingletonResponse>(`over-advances/${overAdvanceId}`, payload)
    .then((res) => {
      return res.data.overAdvance
    })
  return data
}

export const getApproveForFundingSummaryOverAdvances = async (params: OverAdvanceApproveForFundingSummaryParams) => {
  const data = await request
    .get<ApproveForFundingOverAdvancesSummaryResponse>('over-advances/approve-for-funding-summary', {
      params: params,
    })
    .then((res) => {
      return res.data
    })
  return data
}

export const approveForFundingOverAdvances = async (payload: ApproveForFundingOverAdvancesRequest) => {
  const data = await request
    .patch<InvoiceGroupSingletonResponse>('over-advances/approve-for-funding', payload)
    .then((res) => {
      return res.data.invoiceGroup
    })
  return data
}

export const getOverAdvanceDocuments = async (
  overAdvanceId: string,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM,
  sort: string
) => {
  const data = await request
    .get<DocumentListResponse>(`over-advances/${overAdvanceId}/documents`, {
      params: {
        sort: sort,
      },
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => {
      return { data: res.data, headers: res.headers }
    })
  return data
}

export const updatePlannedPayment = async (
  overAdvanceId: string,
  paymentId: string,
  payload: UpdatePlannedPaymentRequest
): Promise<OverAdvanceInfo> => {
  return await request
    .patch<OverAdvanceSingletonResponse>(`over-advances/${overAdvanceId}/planned-payments/${paymentId}`, payload)
    .then((res) => res.data.overAdvance)
}

export const regeneratePaymentPlan = async (
  overAdvanceId: string,
  payload: RegeneratePaymentPlanRequest
): Promise<OverAdvanceInfo> => {
  return await request
    .put<OverAdvanceSingletonResponse>(`over-advances/${overAdvanceId}/payment-plan`, payload)
    .then((res) => res.data.overAdvance)
}

export const findOpenChargebackOverAdvances = async (companyId: string): Promise<OverAdvanceInfo[]> => {
  const data = await request
    .get<OverAdvanceGetCollectionResponse>(`over-advances/${companyId}/open-for-chargeback`)
    .then((res) => {
      return res.data.overAdvances
    })
  return data
}

export const findOpenOverAdvancesForPayment = async (
  params: OverAdvanceListParams,
  pageNumber: number = PAGINATION_NUM_VALUE_NUM,
  pageLimit: number = PAGINATION_LIMIT_VALUE_NUM
) => {
  if (params && params.q) {
    params.q = params.q.trim()
  }
  const data = await request
    .get<OverAdvanceGetCollectionResponse>('over-advances', {
      params,
      headers: getPaginationHeaders(pageNumber, pageLimit),
    })
    .then((res) => ({
      headers: res.headers,
      data: res.data.overAdvances,
    }))
  return data
}

export const getOverAdvancePayoff = async (overAdvanceId: string) => {
  return await request.get<OverAdvancePayoff>(`over-advances/${overAdvanceId}/payoff`).then((res) => res.data)
}

export const closeOverAdvance = async (overAdvanceId: string) => {
  return await request.post<OverAdvanceSingletonResponse>(`over-advances/${overAdvanceId}/close`).then((res) => res.data)
}
